import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useReduxState, useUpdateSession } from 'hooks';
import { clearAuthenticatedSession } from 'lib/auth';
import { Alert } from 'components/common';
import { Modal, ModalContent, ModalPortal } from 'components/common/modal';
import { AuthStrategyFormLogin, LDAPLogin, LocalLogin, SingleSignOn } from '.';

const initialState = { user: null, visible: false };

const SessionTimeout = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: { authStrategies },
    theme,
    user
  } = useReduxState();

  const checkSessionTimeout = useRef<NodeJS.Timeout>(null);

  const router = useRouter();

  const [state, setState] = useState(initialState);

  useUpdateSession({ enabled: Boolean(user) });

  useEffect(() => {
    try {
      if (sessionStorage) {
        sessionStorage.setItem('vuoSessionUpdated', Date.now().toString());
      }
    } catch (err) {
      console.error(err);
    }

    checkSessionExpires();

    return () => clearTimeout(checkSessionTimeout.current);
  }, []);

  const checkSessionExpires = () => {
    if (!user) return;

    const expiredUpdateTime = Date.now() - 1770000; // 29 minutes, 30 seconds
    let sessionUpdated = null;
    if (sessionStorage) {
      sessionUpdated = Number(sessionStorage.getItem('vuoSessionUpdated'));
    }
    if (sessionUpdated && sessionUpdated < expiredUpdateTime) {
      // session has expired
      setState({ user, visible: true });
      sessionStorage.setItem('isVuoSessionExpired', 'true');
    } else {
      // check expiry once every minute
      checkSessionTimeout.current = setTimeout(checkSessionExpires, 60000);
    }
  };

  const handleLoginSuccess = () => {
    sessionStorage.removeItem('isVuoSessionExpired');

    // delete login notification cookie if it exists, we do not want to
    // show it if local user reauthenticates through this modal
    document.cookie =
      'showLoginNotification=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

    setState(initialState);
    checkSessionExpires();
  };

  const handleBackToLogin = async () => {
    sessionStorage.removeItem('isVuoSessionExpired');

    setState(initialState);
    await clearAuthenticatedSession(dispatch);
    router.push('/login');
  };

  if (
    !state.user ||
    !state.user?.roles?.includes('user') ||
    (state.user?.twoFactorAuthIsEnabled &&
      state.user?.authenticatorType === 'local')
  ) {
    return null;
  }

  const strategy = authStrategies.find(
    (str) => str.name === state.user.authenticator
  );

  const infoText = ['api-endpoint', 'ldap', 'local', 'local-totp'].includes(
    state.user.authenticatorType
  )
    ? messages.authSessionTimeoutLocalInfo
    : messages.authSessionTimeoutDefaultInfo;

  const next = window.location.href.replace(window.location.origin, '');

  return (
    <ModalPortal>
      <div className="session-timeout">
        <Modal
          title={messages.authSessionTimeout}
          visible={state.visible}
          width="narrow"
        >
          <ModalContent>
            <Alert text={infoText} />

            {['local', 'local-totp'].includes(state.user.authenticatorType) ? (
              <div className="local-form">
                <LocalLogin
                  isOpen={true}
                  onSuccess={handleLoginSuccess}
                  sessionUserUsername={state.user.username}
                />
              </div>
            ) : state.user.authenticator &&
              strategy &&
              !strategy.hideFromLogin ? (
              strategy.type === 'api-endpoint' ? (
                <div className="local-form">
                  <AuthStrategyFormLogin
                    isOpen={true}
                    next={next}
                    onSuccess={handleLoginSuccess}
                    sessionUserEmail={state.user.email}
                    strategy={strategy}
                  />
                </div>
              ) : strategy.type === 'ldap' ? (
                <div className="local-form">
                  <LDAPLogin
                    isOpen={true}
                    next={next}
                    onSuccess={handleLoginSuccess}
                    sessionUserEmail={state.user.email}
                    strategy={strategy}
                  />
                </div>
              ) : (
                <SingleSignOn next={next} strategy={strategy} />
              )
            ) : null}

            <div className="back-to-login">
              <a onClick={handleBackToLogin}>{messages.authBackToLogin}</a>
            </div>
          </ModalContent>
        </Modal>

        <style jsx>{`
          .local-form {
            margin-top: 15px;
          }
          .back-to-login {
            margin-top: 15px;
            text-align: center;
          }
          .back-to-login a {
            color: ${theme.linkColor};
            cursor: pointer;
            font-weight: 600;
            text-decoration: none;
          }
          .back-to-login a:hover {
            text-decoration: underline;
          }
        `}</style>
      </div>
    </ModalPortal>
  );
};

export default SessionTimeout;
